import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Constants from "../../components/Constants";
import { buildShareData } from "../../components/ShareModal";
import { callExternalApi } from "../../components/external-api.service";

export const useDetail = () => {
  const { user, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const { guid } = useParams();
  const navigate = useNavigate();

  const encodedUser = Constants.encodeUser(user);
  const [actionsDropdownIsActive, setActionsDropdownIsActive] = useState(false);
  // recipe data

  const [recipe, setRecipe] = useState(null);

  const [userOwnsThisRecipe, setUserOwnsThisRecipe] = useState(false);

  // UI state
  const [shareModalIsActive, setShareModalIsActive] = useState(false);
  const [deleteModalIsActive, setDeleteModalIsActive] = useState(false);
  const [variantModalIsActive, setVariantModalIsActive] = useState(false);
  const [disclaimerModalIsActive, setDisclaimerModalIsActive] = useState(false);
  const [willCopyThisRecipeModalIsActive, setWillCopyThisRecipeModalIsActive] =
    useState(false);
  const [errorModalIsActive, setErrorModalIsActive] = useState(false);
  const [sharedFromModalIsActive, setSharedFromModalIsActive] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [errorTitle, setErrorTitle] = useState(null);
  const [guidToEdit, setGuidToEdit] = useState(null);

  //functions
  const handleActionsClick = () => {
    setActionsDropdownIsActive(!actionsDropdownIsActive);
  };

  const loadRecipeDetail = async () => {
    const accessToken = await Constants.getAccessTokenSilentlyWithTimeout(
      getAccessTokenSilently,
      loginWithRedirect,
      navigate
    );
    const url = Constants.generateSecureApiUrl(
      Constants.APIG_GET_RESOURCE,
      `user=${encodedUser}&guid=${guid}`
    );
    
    let { data, error } = await callExternalApi(url, accessToken);

    if (error) {
      setErrorTitle("Error");
      if (error.code === "RECIPE_NOT_FOUND") {
        setErrorMessage("That recipe does not exist or has recently been deleted.");
      } else if (error.code === "FORBIDDEN") {
        setErrorMessage("You don't have permission to view this recipe.");
      } else {
        setErrorMessage("Failed to load recipe.");
      }
      setErrorModalIsActive(true);
      return;
    }

    const r2 = Constants.trueUpRecipe(data);
    
    setRecipe(r2);
    setUserOwnsThisRecipe(r2.userid === encodedUser);

    if (r2.vc === 0 && r2.sf) {
      setSharedFromModalIsActive(true);
    }
  };

  // Share like the mobile app: hand the link to the browser's native share
  // sheet. Where there isn't one (e.g. desktop Firefox), or it refuses, fall
  // back to ShareModal (copy link / email).
  const shareRecipe = async () => {
    const data = buildShareData(guid, recipe?.recipe_name);
    if (navigator.share && (!navigator.canShare || navigator.canShare(data))) {
      try {
        await navigator.share(data);
        return;
      } catch (e) {
        if (e.name === "AbortError") {
          return; // user closed the share sheet
        }
      }
    }
    setShareModalIsActive(true);
  };

  function generateSharedRecipeMessage(sharer) {
    return sharer + " has shared this recipe with you!";
  }

  function navigateToEdit(g) {
    navigate(Constants.ROUTE_EDIT + g);
  }

  const prepareToEdit = async () => {
    // if user owns the recipe, simply redirect to the 'Edit' component
    if (userOwnsThisRecipe) {
      navigateToEdit(guid);
      return;
    }

    // if user doesn't own the recipe, pop up a modal explaining that we'll make a copy of the recipe
    setWillCopyThisRecipeModalIsActive(true);

    // ask back end to create a copy of this recipe, with this user as the owner, then redirect to Edit component
    const new_guid = Constants.generateGUID();
    setGuidToEdit(new_guid);
    const url = Constants.generateSecureApiUrl(
      Constants.APIG_COPY_RESOURCE,
      "&old_guid=" + guid + "&new_guid=" + new_guid + "&user=" + encodedUser
    );
    const accessToken = await Constants.getAccessTokenSilentlyWithTimeout(
      getAccessTokenSilently,
      loginWithRedirect,
      navigate
    );
    const { error } = await callExternalApi(url, accessToken);
    if (error) {
      setWillCopyThisRecipeModalIsActive(false);
      setErrorTitle("Error");
      setErrorMessage("Failed to copy recipe. Please try again.");
      setErrorModalIsActive(true);
      return;
    }
    navigateToEdit(new_guid);
  };

  useEffect(() => {
    let isMounted = true;
    const loadDataWrapper = async () => {
      await loadRecipeDetail();
      if (!isMounted) {
        return;
      }
    };
    loadDataWrapper();
    return () => {
      isMounted = false;
    };
  }, [getAccessTokenSilently, guid]);


   
  return {
    guid,
    actionsDropdownIsActive,
    handleActionsClick,
    setActionsDropdownIsActive,
    recipe,
    errorModalIsActive,
    errorTitle,
    errorMessage,
    setErrorModalIsActive,
    navigate,
    setVariantModalIsActive,
    setShareModalIsActive,
    shareRecipe,
    prepareToEdit,
    userOwnsThisRecipe,
    setDeleteModalIsActive,
    setDisclaimerModalIsActive,
    shareModalIsActive,
    deleteModalIsActive,
    variantModalIsActive,
    willCopyThisRecipeModalIsActive,
    setWillCopyThisRecipeModalIsActive,
    navigateToEdit,
    guidToEdit,
    disclaimerModalIsActive,
    sharedFromModalIsActive,
    setSharedFromModalIsActive,
    generateSharedRecipeMessage,
  };
};
